/**
 * useMarketplacePrefill Hook
 * 
 * Custom hook that handles automatic prefilling of marketplace app IDs
 * (Apple App Store ID and Android Package Name) based on selected tracker
 * and MMP provider.
 * 
 * @description
 * This hook extracts app IDs from the tracker's sources when:
 * - A tracker is selected
 * - MMP provider changes (adjust/appsflyer)
 * - Store type is added (apple/android)
 * 
 * It preserves user edits and handles edit mode correctly.
 */

import { useEffect, useRef } from "react";
import type {
    Tracker,
    MarketplaceIds,
    MMPProvider,
    StoreType,
} from "./ConnectionsStep.types";
import {
    extractMarketplaceIds,
    detectChanges,
} from "./ConnectionsStep.helpers";
import { FORM_FIELD_NAMES } from "./ConnectionsStep.constants";

interface UseMarketplacePrefillParams {
    /** Currently selected tracker ID */
    selectedTrackerID: number | null;
    /** Currently selected MMP provider */
    mmpField: MMPProvider | "";
    /** Currently selected store types */
    selectedAppTypes: StoreType[] | undefined;
    /** Full tracker object (resolved from trackers list) */
    populatedTracker: Tracker | undefined;
    /** react-hook-form watch function */
    watch: (name: string) => unknown;
    /** react-hook-form setValue function */
    setValue: (name: string, value: unknown, options?: { shouldDirty?: boolean }) => void;
}

interface UseMarketplacePrefillReturn {
    /** Currently prefilled values (for use in form resets) */
    prefilledValues: MarketplaceIds;
}

/**
 * Hook for automatically prefilling marketplace IDs from tracker sources
 * 
 * Prefill strategy:
 * - When tracker or MMP changes: Always prefill (user is switching context)
 * - When store type is added: Only prefill if current value is empty
 * - On initial load (edit mode): Skip prefill to preserve existing values
 */
export const useMarketplacePrefill = ({
    selectedTrackerID,
    mmpField,
    selectedAppTypes,
    populatedTracker,
    watch,
    setValue,
}: UseMarketplacePrefillParams): UseMarketplacePrefillReturn => {
    // Track if this is the initial load to avoid overwriting existing values in edit mode
    const isInitialLoadRef = useRef(true);
    
    // Track previous values to detect changes
    const prevTrackerIdRef = useRef<number | null>(selectedTrackerID);
    const prevMmpRef = useRef<MMPProvider | "">(mmpField);
    const prevAppTypesRef = useRef<StoreType[] | undefined>(selectedAppTypes);
    
    // Store prefilled values to preserve them during form resets
    const prefilledValuesRef = useRef<MarketplaceIds>({ ios: null, android: null });

    // Main prefill effect
    useEffect(() => {
        // Skip if no tracker or MMP selected, or MMP is "none"
        if (!selectedTrackerID || !mmpField || mmpField === "none") {
            return;
        }

        // Skip prefill on initial load if we're in edit mode and have existing values
        if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
            prevTrackerIdRef.current = selectedTrackerID;
            prevMmpRef.current = mmpField;
            prevAppTypesRef.current = selectedAppTypes;
            return;
        }

        // Detect what has changed
        const changes = detectChanges(
            prevTrackerIdRef.current,
            selectedTrackerID,
            prevMmpRef.current,
            mmpField,
            prevAppTypesRef.current,
            selectedAppTypes
        );

        // Update refs after checking
        prevTrackerIdRef.current = selectedTrackerID;
        prevMmpRef.current = mmpField;
        prevAppTypesRef.current = selectedAppTypes;

        // Only prefill if something relevant changed
        if (!changes.hasRelevantChanges) {
            return;
        }

        // Extract marketplace IDs from the selected tracker based on MMP
        const marketplaceIds = extractMarketplaceIds(populatedTracker, mmpField);

        // Store prefilled values for later use (e.g., form resets)
        if (marketplaceIds.ios) {
            prefilledValuesRef.current.ios = marketplaceIds.ios;
        }
        if (marketplaceIds.android) {
            prefilledValuesRef.current.android = marketplaceIds.android;
        }

        // Get current form values
        const currentApple = watch(FORM_FIELD_NAMES.APPLICATIONS.APPLE) as string | undefined;
        const currentAndroid = watch(FORM_FIELD_NAMES.APPLICATIONS.ANDROID) as string | undefined;

        // Check if store types are currently selected
        const nowHasApple = selectedAppTypes?.includes("apple") ?? false;
        const nowHasAndroid = selectedAppTypes?.includes("android") ?? false;

        // Prefill iOS app ID if:
        // - We have a marketplace ID AND store type is selected AND
        // - (Tracker/MMP changed OR store type was just added with empty value)
        if (marketplaceIds.ios && nowHasApple) {
            const shouldPrefill = 
                changes.trackerChanged || 
                changes.mmpChanged || 
                (changes.appleAdded && !currentApple);

            if (shouldPrefill) {
                setValue(FORM_FIELD_NAMES.APPLICATIONS.APPLE, marketplaceIds.ios, {
                    shouldDirty: true,
                });
            }
        }

        // Prefill Android app ID with same logic
        if (marketplaceIds.android && nowHasAndroid) {
            const shouldPrefill = 
                changes.trackerChanged || 
                changes.mmpChanged || 
                (changes.androidAdded && !currentAndroid);

            if (shouldPrefill) {
                setValue(FORM_FIELD_NAMES.APPLICATIONS.ANDROID, marketplaceIds.android, {
                    shouldDirty: true,
                });
            }
        }
    }, [selectedTrackerID, mmpField, populatedTracker, selectedAppTypes, watch, setValue]);

    return {
        prefilledValues: prefilledValuesRef.current,
    };
};

export default useMarketplacePrefill;

